import { blogImagePath, extractExcerpt, posts } from "./blogData.ts";
import hljs from "highlight.js/lib/core";
import hljsBash from "highlight.js/lib/languages/bash";
import hljsDockerfile from "highlight.js/lib/languages/dockerfile";
import hljsYaml from "highlight.js/lib/languages/yaml";
import hljsTs from "highlight.js/lib/languages/typescript";
import "highlight.js/styles/github.css";
import { ComponentChildren } from "preact";
import { useEffect, useRef, useState } from "preact/hooks";
import { useRoute } from "preact-iso";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { usePageMeta } from "./pageMeta.ts";
import { codeChildProps } from "./components.tsx";

export { blogImagePath, extractExcerpt, posts };

hljs.registerLanguage("typescript", hljsTs);
hljs.registerLanguage("bash", hljsBash);
hljs.registerLanguage("dockerfile", hljsDockerfile);
hljs.registerLanguage("yaml", hljsYaml);

const darkCodeTheme = `.dark .hljs { color: #c9d1d9; background: #0d1117 }
  .dark .hljs-keyword, .dark .hljs-type { color: #ff7b72 }
  .dark .hljs-title.function_ { color: #d2a8ff }
  .dark .hljs-attr, .dark .hljs-number, .dark .hljs-variable { color: #79c0ff }
  .dark .hljs-string { color: #a5d6ff }
  .dark .hljs-built_in { color: #ffa657 }
  .dark .hljs-comment { color: #8b949e }
  .dark .hljs-name { color: #7ee787 }`;

if (typeof document !== "undefined") {
  const darkStyleEl = document.createElement("style");
  darkStyleEl.textContent = darkCodeTheme;
  document.head.appendChild(darkStyleEl);
}

const authorName = "Uri Walevski";
const authorUrl = "https://www.linkedin.com/in/uriv/";

const hljsLang = (lang: string) => hljs.getLanguage(lang) ? lang : undefined;

const HighlightedCode = (
  { code, lang }: { code: string; lang?: string },
) => {
  const ref = useRef<HTMLElement>(null);
  useEffect(() => {
    if (!ref.current) return;
    const resolved = lang ? hljsLang(lang) : undefined;
    ref.current.innerHTML = resolved
      ? hljs.highlight(code.trim(), { language: resolved }).value
      : code.trim().replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(
        />/g,
        "&gt;",
      );
  }, [code, lang]);
  return (
    <pre className="overflow-auto text-sm p-4 bg-surface-light dark:bg-surface-raised rounded-lg border border-border-subtle-light dark:border-border-subtle font-mono leading-relaxed my-6">
      <code ref={ref} />
    </pre>
  );
};

const markdownComponents = {
  h2: ({ children }: { children?: ComponentChildren }) => (
    <h2 className="text-2xl font-display font-semibold mt-10 mb-4 text-text-primary-light dark:text-text-primary">
      {children}
    </h2>
  ),
  h3: ({ children }: { children?: ComponentChildren }) => (
    <h3 className="text-xl font-display font-semibold mt-8 mb-3 text-text-primary-light dark:text-text-primary">
      {children}
    </h3>
  ),
  p: ({ children }: { children?: ComponentChildren }) => (
    <p className="mb-5 text-text-secondary-light dark:text-text-secondary leading-7 text-base">
      {children}
    </p>
  ),
  a: (
    { href, children }: { href?: string; children?: ComponentChildren },
  ) => (
    <a
      href={href}
      className="text-accent-light dark:text-accent underline"
      target="_blank"
      rel="noopener noreferrer"
    >
      {children}
    </a>
  ),
  strong: ({ children }: { children?: ComponentChildren }) => (
    <strong className="font-semibold text-text-primary-light dark:text-text-primary">
      {children}
    </strong>
  ),
  em: ({ children }: { children?: ComponentChildren }) => (
    <em className="italic">{children}</em>
  ),
  blockquote: ({ children }: { children?: ComponentChildren }) => (
    <blockquote className="border-l-4 border-border-default-light dark:border-border-default pl-4 my-6 text-text-secondary-light dark:text-text-secondary italic">
      {children}
    </blockquote>
  ),
  ul: ({ children }: { children?: ComponentChildren }) => (
    <ul className="list-disc pl-6 mb-5 space-y-2 text-text-secondary-light dark:text-text-secondary text-base leading-7">
      {children}
    </ul>
  ),
  ol: ({ children }: { children?: ComponentChildren }) => (
    <ol className="list-decimal pl-6 mb-5 space-y-2 text-text-secondary-light dark:text-text-secondary text-base leading-7">
      {children}
    </ol>
  ),
  li: ({ children }: { children?: ComponentChildren }) => <li>{children}</li>,
  pre: ({ children }: { children?: ComponentChildren }) => {
    const { className = "", children: code = "" } = codeChildProps(children);
    const lang = className.replace("language-", "");
    return <HighlightedCode code={String(code)} lang={lang || undefined} />;
  },
  code: ({ children }: { children?: ComponentChildren }) => (
    <code className="bg-surface-overlay-light dark:bg-surface-raised px-1.5 py-0.5 rounded text-sm font-mono text-text-primary-light dark:text-text-primary">
      {children}
    </code>
  ),
};

const postContainerStyle =
  "max-w-2xl lg:max-w-4xl mx-auto my-10 px-6 py-8 rounded-lg shadow bg-surface-raised-light dark:bg-surface-raised text-text-primary-light dark:text-text-primary";

const shimmerBg =
  "bg-surface-overlay-light dark:bg-surface-overlay bg-[length:200%_100%] animate-[shimmer_1.5s_infinite]";

const shimmerGradient =
  "bg-[linear-gradient(90deg,transparent_0%,rgba(255,255,255,0.08)_50%,transparent_100%)]";

const ShimmerImg = (
  { src, alt, className, onLoad }: {
    src: string;
    alt: string;
    className: string;
    onLoad: () => void;
  },
) => {
  const ref = useRef<HTMLImageElement>(null);
  useEffect(() => {
    if (ref.current?.complete) onLoad();
  }, []);
  return (
    <img
      ref={ref}
      src={src}
      alt={alt}
      className={className}
      loading="lazy"
      onLoad={onLoad}
    />
  );
};

export const ThemedBlogImage = (
  { slug, alt, className }: { slug: string; alt: string; className?: string },
) => {
  const [loaded, setLoaded] = useState(false);
  const imgClass = `${className ?? ""} transition-opacity duration-300 ${
    loaded ? "opacity-100" : "opacity-0"
  }`;
  return (
    <div className={`relative ${className ?? ""}`}>
      {!loaded && (
        <div
          className={`absolute inset-0 rounded-lg ${shimmerBg} ${shimmerGradient}`}
        />
      )}
      <ShimmerImg
        src={blogImagePath(slug, "light")}
        alt={alt}
        className={`dark:hidden ${imgClass}`}
        onLoad={() => setLoaded(true)}
      />
      <ShimmerImg
        src={blogImagePath(slug, "dark")}
        alt={alt}
        className={`hidden dark:block ${imgClass}`}
        onLoad={() => setLoaded(true)}
      />
    </div>
  );
};

const FeaturedPost = ({
  slug,
  title,
  date,
  content,
}: {
  slug: string;
  title: string;
  date: string;
  content: string;
}) => (
  <a
    href={`/blog/${slug}`}
    className="block group overflow-hidden rounded-lg"
  >
    <ThemedBlogImage
      slug={slug}
      alt={title}
      className="w-full aspect-[3/2] object-cover"
    />
    <div className="mt-6">
      <h2 className="text-2xl lg:text-3xl font-bold text-text-primary-light dark:text-text-primary group-hover:text-accent-light dark:group-hover:text-accent transition-colors">
        {title}
      </h2>
      <p className="text-sm text-text-tertiary-light dark:text-text-tertiary mt-2">
        {date}
      </p>
      <p className="text-base text-text-secondary-light dark:text-text-secondary mt-3 leading-relaxed">
        {extractExcerpt(content)}
      </p>
    </div>
  </a>
);

const PostGridCard = ({
  slug,
  title,
  date,
}: {
  slug: string;
  title: string;
  date: string;
}) => (
  <a
    href={`/blog/${slug}`}
    className="block group overflow-hidden rounded-lg"
  >
    <ThemedBlogImage
      slug={slug}
      alt={title}
      className="w-full aspect-[3/2] object-cover"
    />
    <div className="mt-4">
      <h3 className="text-lg font-semibold text-text-primary-light dark:text-text-primary group-hover:text-accent-light dark:group-hover:text-accent transition-colors leading-snug">
        {title}
      </h3>
      <p className="text-xs text-text-tertiary-light dark:text-text-tertiary mt-1">
        {date}
      </p>
    </div>
  </a>
);

export const Blog = () => {
  usePageMeta({
    title: "Blog | prompt2bot",
    description:
      "Updates, tutorials, and behind-the-scenes posts from the prompt2bot team.",
  });
  const [featured, ...rest] = posts;
  return (
    <div className="flex-grow">
      <div className={postContainerStyle}>
        <h1 className="text-2xl font-bold mb-8 text-center">Blog</h1>
        <FeaturedPost {...featured} />
        {rest.length > 0 && (
          <div className="mt-12">
            <h2 className="text-xl font-bold mb-6 text-text-primary-light dark:text-text-primary">
              All Posts
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {rest.map(({ slug, title, date }) => (
                <PostGridCard
                  key={slug}
                  slug={slug}
                  title={title}
                  date={date}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export const BlogPost = () => {
  const { params } = useRoute();
  const post = posts.find(({ slug }) => slug === params.slug);
  usePageMeta(
    post
      ? {
        title: `${post.title} | prompt2bot`,
        description: extractExcerpt(post.content),
        image: blogImagePath(post.slug, "light"),
        type: "article",
        twitterCard: "summary_large_image",
      }
      : {
        title: "Post not found | prompt2bot",
        description: "This blog post does not exist.",
      },
  );
  if (!post) {
    return (
      <div className="flex-grow">
        <div className={postContainerStyle}>
          <p className="text-center text-text-tertiary-light dark:text-text-tertiary">
            Post not found.
          </p>
        </div>
      </div>
    );
  }
  return (
    <div className="flex-grow">
      <div className={postContainerStyle}>
        <ThemedBlogImage
          slug={post.slug}
          alt={post.title}
          className="w-full rounded-lg mb-6"
        />
        <h1 className="text-3xl font-bold mb-2">{post.title}</h1>
        <p className="text-sm text-text-tertiary-light dark:text-text-tertiary mb-1">
          {post.date}
        </p>
        <p className="text-sm text-text-tertiary-light dark:text-text-tertiary mb-8">
          by{" "}
          <a
            href={authorUrl}
            className="text-accent-light dark:text-accent underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            {authorName}
          </a>
        </p>
        {/* @ts-expect-error: Preact VNode vs React ReactNode mismatch */}
        <Markdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
          {post.content}
        </Markdown>
        <a
          href="/blog"
          className="inline-block mt-10 text-sm text-accent-light dark:text-accent hover:underline"
        >
          ← All posts
        </a>
      </div>
    </div>
  );
};
